import { useEffect, useState, type FC, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { PIC } from "@ryde/assets";
import { HR, Label, MuiButton, Select } from "@ryde/controls";
import {
  EditFormMode,
  EntitiesWithoutDuplicateCrudUi,
  useDrawers,
  type IDrawerNewOrDuplicateProps,
  type IDropdownItem,
  type IInjectedDrawerProps,
} from "@ryde/shared";

import {
  BaseDrawer,
  DrawerContentStyled,
  PreCreationBlockContentWrapper,
} from "../drawer";
import { DrawerHeaderWithSubtitle } from "../drawer/header-with-subtitle";
import { useDrawerNewOrDuplicate } from "./hooks";

export const DrawerNewOrDuplicate: FC<
  IDrawerNewOrDuplicateProps & IInjectedDrawerProps
> = ({
  entity,
  resolveFn,
  canResolve,
  onCloseDrawer,
  onCreateCustomAction,
  mode,
  drawerNumber,
  newItemDraft,
}) => {
  const options = useDrawerNewOrDuplicate(entity);
  const { t } = useTranslation();
  const entityNameTranslated = t(`${entity}.single`);
  const showDuplicateFromExisting =
    Boolean(options?.length) &&
    !EntitiesWithoutDuplicateCrudUi.includes(entity);
  const [selectedOptionId, setSelectedOptionId] = useState<
    string | number | boolean
  >("");
  const { openAddDropdownFormDrawer } = useDrawers();

  const onCreateClick = async (idCopyFrom?: number): Promise<void> => {
    onCloseDrawer();

    if (onCreateCustomAction) {
      onCreateCustomAction(idCopyFrom);
    } else {
      resolveFn?.(
        await openAddDropdownFormDrawer({
          entity,
          idCopyFrom,
          canResolve,
          newItemDraft: idCopyFrom ? undefined : newItemDraft,
        }),
      );
    }
  };

  const onOptionSelect = (value: unknown): void => {
    setSelectedOptionId((value as IDropdownItem)?.value);
  };

  const onDuplicateClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();

    if (selectedOptionId) {
      onCreateClick(Number(selectedOptionId));
    }
  };

  const create = (): void => {
    onCreateClick();
  };

  useEffect(() => {
    if (options?.length) {
      setSelectedOptionId(options[0].value);
    }
  }, [options]);

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <DrawerHeaderWithSubtitle
        title={t(`general.ADD_NEW`, { entity: entityNameTranslated })}
        subTitle={
          mode === EditFormMode.DropdownAddDrawer
            ? ` (${t("general.DROPDOWN")}) `
            : ""
        }
        onCloseDrawer={onCloseDrawer}
      />
      <DrawerContentStyled className="drawer-form-with-image">
        <PreCreationBlockContentWrapper
          data-e2e-entity={entity}
          className="pre-creation-drawer__content"
        >
          {showDuplicateFromExisting && (
            <div className="fullWidth">
              <h3>
                {t("general.USE_EXISTING", { entity: entityNameTranslated })}
              </h3>

              <Label>
                {t("general.CHOOSE_DUPLICATE", {
                  entity: entityNameTranslated,
                })}
              </Label>
              <Select
                data-e2e-id={`${entity}_pre_creation_select_entity_name`}
                defaultValue={options[0]}
                options={options}
                onChange={onOptionSelect}
                width="100%"
              />

              <div className="link">
                <MuiButton
                  e2eData="button-duplicate"
                  onClick={onDuplicateClick}
                >
                  {t("general.DUPLICATE", { entity: entityNameTranslated })}
                  <span>
                    <PIC.ArrowInCircle />
                  </span>
                </MuiButton>
              </div>

              <HR />
              <h3 className="bold">{t("general.OR")}</h3>
              <HR />
            </div>
          )}
          <h3>{t("general.CREATE_NEW", { entity: entityNameTranslated })}</h3>

          <MuiButton
            e2eData={`${entity}_pre_creation_button_new`}
            muiStyle={"primary"}
            muiSize={"small"}
            onClick={create}
          >
            {t("general.NEW_ENTITY", { entity: entityNameTranslated })}
          </MuiButton>
        </PreCreationBlockContentWrapper>
      </DrawerContentStyled>
    </BaseDrawer>
  );
};
